import {
  LinkTargets,
  StoreLocationConfig,
  type StoreMapFilterData,
  StoreMapFilterSubGroup,
  StoreMapFilterSubGroupOption
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  type Store,
  type StoreCluster
} from '../../../../services/stores/stores-sdk'
import { StoreImage, StoreMapProps } from './types'
import {
  buildDataLayerStoreObj,
  calculateStoreDistance,
  createStoreDetailUrl,
  getCategoryDetails,
  getCurrentDayOpenCloseMessage,
  getFormattedAddress,
  getOpeningHours,
  getPaymentMethods,
  getStoreDistanceInMeters,
  getStoreServices
} from '../../StoreDetails/utils'
import { OpenStatus } from '../../StoreDetails/types'
import {
  FilterGroups,
  SubGroups
} from '../StoreMapFilter/StoreMapFilterEditConfig'
import { pushDataLayer } from '../../../../utils/datalayer'
import {
  getCoresiteInteractions,
  updateCoresiteInteractions
} from '../../../../utils/local-storage/interactions'
import { validateStoreImage } from '../common/store-image-utils'

export const fallbackDays = {
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
  sunday: ''
}

export const OPEN_NOW = 'OPENNOW'

const buildStoreAddress = (
  address: StoreCluster['topStore']['address'],
  storeAddressFormatTemplate?: string
) => {
  if (!address) {
    return undefined
  }

  return {
    addressLine1: address.addressLine1 ?? '',
    postalCode: address.postalCode ?? '',
    city: address.city ?? '',
    countryCode: address.countryCode ?? '',
    formattedAddress: getFormattedAddress(address, storeAddressFormatTemplate),
    location: address.location
      ? {
          latitude: address.location.latitude ?? 0,
          longitude: address.location.longitude ?? 0
        }
      : {
          latitude: 0,
          longitude: 0
        }
  }
}

export const mapPositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 300000,
  timeout: 10000
}

export const radiusByZoom = [
  7392, // 0
  7392, // 1
  7392, // 2
  7392, // 3
  7392, // 4
  3697, // 5
  1848, // 6
  924, // 7
  462, // 8
  231, // 9
  115, // 10
  57, // 11
  28, // 12
  14, // 13
  7, // 14
  4, // 15
  2, // 16
  1, // 17
  0.5, // 18
  0.3, // 19
  0.1, // 20
  0.01, // 21
  0.001 // 22
]

export const bookAppointmentHandler = (topStore: Store) => () => {
  pushDataLayer([
    { key: 'event', value: 'journey_event' },
    { key: 'journeyName', value: 'book appointment' },
    { key: 'stepName', value: 'start' }
  ])

  const coresiteInteractions = getCoresiteInteractions()
  updateCoresiteInteractions({
    booking: {
      ...coresiteInteractions?.booking,
      selectedStore: topStore
    }
  })
}

export async function transformStoreLocationsData({
  storeLocations,
  storeImage,
  setZoom,
  storeLocatorConfig,
  userLocation,
  storeTypeOrder,
  enableMilesAsDistanceUnit,
  isSsr = false,
  storeDetailsConfig,
  isUserLoggedIn,
  selectedOpening = []
}: {
  storeLocations: StoreCluster[]
  storeImage?: StoreImage
  setZoom: (zoom: number) => void
  storeLocatorConfig?: StoreMapProps['storeLocatorConfig']
  userLocation: {
    latitude: number
    longitude: number
  }
  storeTypeOrder?: string
  enableMilesAsDistanceUnit?: boolean
  isSsr?: boolean
  storeDetailsConfig?: StoreMapProps['storeDetailsConfig']
  isUserLoggedIn?: boolean
  selectedOpening?: string[]
}): Promise<StoreLocationConfig[]> {
  const {
    categories,
    days,
    openedUntilLabel = '',
    willOpenAtLabel = '',
    openingHoursNotAvailable = '',
    enable12HourFormat,
    enableExpandedOpeningHoursView,
    servicesListTitle,
    services,
    distanceLabel,
    closedLabel = '',
    openingHoursTitle,
    showDirectionsLabel,
    showBookAnAppointmentServiceEntry,
    bookAnAppointmentLabel,
    bookAnAppointmentLink,
    loginPageLink,
    mapClusterIcon,
    mapHoverClusterIcon,
    storeDetailPageUrl,
    storeAddressFormatTemplate
  } = storeLocatorConfig ?? {}

  const { paymentMethodsTitle, payments } = storeDetailsConfig ?? {}

  const shouldShowBookAnAppointment = !!(
    showBookAnAppointmentServiceEntry &&
    bookAnAppointmentLabel &&
    bookAnAppointmentLink &&
    loginPageLink
  )

  const typeOrderArray = storeTypeOrder ? storeTypeOrder.split('|') : []

  const mappedStores: Array<
    StoreLocationConfig & {
      distanceInMeters?: number
      storeId?: string
      type?: string
    }
  > = await Promise.all(
    storeLocations?.map(
      async ({
        storeCount,
        centroid,
        topStore
      }: StoreCluster): Promise<
        StoreLocationConfig & {
          distanceInMeters?: number
          storeId?: string
          type?: string
        }
      > => {
        const category = getCategoryDetails(
          categories ?? [],
          topStore.type ?? ''
        )
        const currentDayInfo = getCurrentDayOpenCloseMessage(
          topStore?.openingHours ?? [],
          {
            labels: {
              openedUntilLabel,
              willOpenAtLabel,
              openingHoursNotAvailable
            },
            days: days ?? fallbackDays,
            enable12HourFormat
          }
        )

        const enabledServices = getStoreServices(
          services ?? [],
          topStore?.services ?? []
        )

        const paymentMethods = getPaymentMethods(
          payments ?? [],
          topStore?.paymentMethods ?? []
        )

        const openingHoursContent = getOpeningHours(
          (topStore?.openingHours ?? []) as Array<{
            openingTime: string | null
            closingTime: string | null
            isClosed?: boolean
            dayOfWeek: {
              index: number
              name: string
            }
          }>,
          {
            closedLabel,
            enable12HourFormat,
            enableExpandedOpeningHoursView,
            days: days ?? fallbackDays
          }
        )

        const storeServicesProps =
          enabledServices.length > 0
            ? {
                title: servicesListTitle,
                services: enabledServices
              }
            : undefined

        const paymentMethodsProps =
          paymentMethods.length > 0
            ? {
                title: paymentMethodsTitle,
                paymentMethods: payments
              }
            : undefined

        const openingHoursProps =
          openingHoursContent.length > 0
            ? {
                title: openingHoursTitle,
                openingHours: openingHoursContent
              }
            : undefined

        const categorySvgIcon = category?.hoveredImage ?? category?.image ?? ''
        const clusterSvgIcon = mapHoverClusterIcon ?? mapClusterIcon ?? ''
        const shouldShowClusterIcon = storeCount > 1

        const distance = calculateStoreDistance(centroid, userLocation, {
          enableMilesAsDistanceUnit,
          isSsr
        })
        const appointmentLinkUrl = isUserLoggedIn
          ? bookAnAppointmentLink
          : `${
              loginPageLink
            }?referrer=${encodeURIComponent(bookAnAppointmentLink ?? '')}`

        const distanceInMeters = getStoreDistanceInMeters(
          centroid,
          userLocation,
          isSsr
        )

        const linkUrl = createStoreDetailUrl(topStore, storeDetailPageUrl ?? '')

        const validatedUrl = await validateStoreImage(
          topStore,
          storeImage?.image?.src ?? ''
        )
        const shouldIncludeStoreImage =
          storeImage && validatedUrl && validatedUrl !== storeImage?.image?.src

        return {
          onClick(_store, zoom) {
            if (zoom) {
              setZoom(zoom)
            }
          },
          storeCount,
          isOfficialCluster: shouldShowClusterIcon,
          centroid,
          linkUrl,
          storePinSvgUrl: shouldShowClusterIcon
            ? clusterSvgIcon
            : categorySvgIcon,
          mapClusterIcon: mapClusterIcon ?? '',
          mapHoverClusterIcon: clusterSvgIcon,
          name: topStore?.name,
          distance: distance ? `(${distance})` : undefined,
          address: buildStoreAddress(
            topStore?.address,
            storeAddressFormatTemplate
          ),
          categoryLabel: category?.ariaLabel ?? '',
          categoryIcon: category?.image ?? '',
          categoryHoveredIcon: categorySvgIcon,
          currentDay: currentDayInfo,
          phone: topStore?.phone,
          ...(shouldIncludeStoreImage && {
            storeImage: {
              ...storeImage,
              image: {
                ...storeImage?.image,
                src: validatedUrl,
                srcset: validatedUrl
              }
            }
          }),
          ...(shouldShowBookAnAppointment &&
            topStore?.services?.includes('APPOINTMENTS') && {
              bookAnAppointmentConfig: {
                label: bookAnAppointmentLabel,
                icon: 'pmi-icon-bookings',
                onClick: bookAppointmentHandler(topStore),
                linkUrl: appointmentLinkUrl
              }
            }),
          storePhoneNumberConfig: {
            icon: 'pmi-icon-contact',
            label: topStore?.phone,
            onClick: () =>
              pushDataLayer(
                buildDataLayerStoreObj('StoreLocatorCall', topStore)
              )
          },
          ...(showDirectionsLabel && {
            storeDirectionConfig: {
              label: showDirectionsLabel,
              icon: 'pmi-icon-direction',
              linkTarget: LinkTargets.Blank
            }
          }),
          ...(openingHoursProps ? { openingHours: openingHoursProps } : {}),
          ...(storeServicesProps ? { storeServices: storeServicesProps } : {}),
          ...(paymentMethodsProps
            ? { paymentMethods: paymentMethodsProps }
            : {}),
          storeDistanceContent: distanceLabel,
          distanceInMeters,
          storeId: topStore.storeId,
          type: topStore.type
        }
      }
    )
  )

  // Filter stores based on selectedOpening (OPENNOW filter)
  const filteredStores = selectedOpening.includes(OPEN_NOW)
    ? mappedStores.filter(
        (store) => store.currentDay?.status !== OpenStatus.Closed
      )
    : mappedStores

  // Sort all stores by distance first
  const sortedByDistance = [...filteredStores].sort(
    (a, b) =>
      (a.distanceInMeters ?? Infinity) - (b.distanceInMeters ?? Infinity)
  )

  // If no type ordering specified, return sorted stores without grouping
  if (typeOrderArray.length === 0) {
    return sortedByDistance.map(({ distanceInMeters, ...rest }) => rest)
  }

  // Bucket stores in a single pass
  const typeBuckets = new Map<string, typeof mappedStores>()
  const restStores: typeof mappedStores = []

  typeOrderArray.forEach((type) => typeBuckets.set(type, []))

  sortedByDistance.forEach((store) => {
    const type = store.type ?? ''
    if (typeBuckets.has(type)) {
      typeBuckets.get(type)!.push(store)
    } else {
      restStores.push(store)
    }
  })

  // Concatenate buckets in order: order1 (sorted), order2 (sorted), others (sorted)
  const sortedStores: typeof mappedStores = []
  typeOrderArray.forEach((type) => {
    sortedStores.push(...typeBuckets.get(type)!)
  })
  sortedStores.push(...restStores)

  return sortedStores.map(({ distanceInMeters, ...rest }) => rest)
}
export const mapFilterSubGroupGroupsToStoreMapFilterSubGroup = (
  filterSubGroups: SubGroups[]
): StoreMapFilterSubGroup[] => {
  return (
    filterSubGroups?.map((fsg) => {
      return {
        subGroupId: fsg.subGroupId ?? '',
        subGroupName: fsg.subGroupName ?? '',
        allowMultiselect: fsg.allowMultiselect === 'true',
        filterSubGroupsOptions: (fsg.filterSubGroupsOptions ??
          []) as StoreMapFilterSubGroupOption[]
      }
    }) ?? []
  )
}

export const extractFilterCodes = (filters: StoreMapFilterSubGroupOption[]) => {
  return filters.map((f) => f.filterCode)
}

export const extractFiltersCodesWithFlatMap = (
  filters: StoreMapFilterSubGroupOption[]
) => {
  return filters.flatMap((f) => f.filterCode.split('|').filter(Boolean))
}

export const mapFilterGroupsToStoreMapFilterSubGroup = (
  filterGroups: FilterGroups[]
): StoreMapFilterData[] => {
  return (
    filterGroups?.map((fg) => {
      const filterSubGroups = mapFilterSubGroupGroupsToStoreMapFilterSubGroup(
        fg.filterSubGroups ?? []
      )
      return {
        groupId: fg.groupId ?? '',
        groupName: fg.groupName ? `<b>${fg.groupName}</b>` : '',
        filterSubGroups
      }
    }) ?? []
  )
}

export const transformCAConfigsData = ({
  storeLocatorConfig,
  storeMapConfig,
  storeDetailsConfig
}: {
  storeLocatorConfig: StoreMapProps['storeLocatorConfig']
  storeMapConfig: StoreMapProps['storeMapConfig']
  storeDetailsConfig: StoreMapProps['storeDetailsConfig']
}) => {
  const {
    storeTypeOrder,
    enableMilesAsDistanceUnit,
    initialRadiusValue,
    currentLocationLabel,
    currentLocationAriaLabel,
    zoomInAriaLabel,
    zoomOutAriaLabel,
    storeDetailsLabelOnListView,
    getDirectionsLabelOnListView,
    storeAddressFormatTemplate,
    categories,
    openedUntilLabel,
    willOpenAtLabel,
    days,
    enable12HourFormat
  } = storeLocatorConfig ?? {}
  const {
    inputPlaceholder,
    inputAriaLabel,
    listViewLabel,
    listViewLmaoViewLabel,
    storeInfoUse,
    maxZoomOutLevel
  } = storeMapConfig ?? {}
  const { paymentMethodsTitle, payments } = storeDetailsConfig ?? {}

  return {
    labels: {
      inputPlaceholder,
      inputAriaLabel,
      listViewLabel,
      listViewLmaoViewLabel,
      storeDetailsLabelOnListView,
      getDirectionsLabelOnListView,
      currentLocationLabel,
      currentLocationAriaLabel,
      zoomInAriaLabel,
      zoomOutAriaLabel,
      paymentMethodsTitle,
      openedUntilLabel,
      willOpenAtLabel
    },
    configs: {
      storeTypeOrder,
      enableMilesAsDistanceUnit,
      initialRadiusValue,
      payments,
      storeInfoUse,
      storeAddressFormatTemplate,
      categories,
      days,
      enable12HourFormat,
      maxZoomOutLevel
    }
  }
}
